import { useEffect, useRef, useState, useCallback } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import FilterProduksi from "./FilterProduksi";
import api from '../auth/apiClient';
import {
    Box,
    Flex,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ButtonGroup,
    Heading,
    ModalBody,
    ModalCloseButton,
    Text,
    Grid,
    HStack,
    VStack,
    Spinner,
    Center,
    useBreakpointValue,
} from "@chakra-ui/react";
import { FiPlus, FiMinus } from "react-icons/fi";
import { MdMyLocation, MdOutlineLegendToggle, MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from "react-icons/md";


import { _01_RED_DARK, _02_RED, _05_GOLD, _06_GREEN_YELLOW, _07_GREEN_LIGHT, _08_GREEN_DARK } from "./MapInspection";

interface FilterState {
    jenis_laporan?: string;
    tahun?: string;
    bulan?: string;
    estate?: string;
    tphMode?: "simple" | "trace";
}

interface TphData {
    company_id: number;
    estate_id: number;
    division_id: number;
    block: string;
    tph: string;
    lat: number;
    long: number;
    harvester: string;
    bcc_cnt: number;
    avg_harvest_area: number;
    jjg_qty: number;
    brd_qty: number;
    weight_qty: number;
    bjr: number;
}

interface ProductionData {
    estate: string;
    division: string;
    block: string;
    block_status: string;
    planted_year: string;
    planted_area: number;
    total_planted: number;
    sph: number;
    sph_prc: number;
    harvest_area: number;
    rotation: number;
    hk_act: number;
    hk_bgt: number;
    hk_prc: number;
    jjg_act: number;
    jjg_bgt: number;
    jjg_prc: number;
    weight_act: number;
    weight_bgt: number;
    weight_prc: number;
    yield_act: number;
    yield_bgt: number;
    yield_prc: number;
    bjr_act: number;
    bjr_bgt: number;
    bjr_prc: number;
    brd_qty_act: number;
    brd_prc: number;
    lebih_basis_prc: number;
    hk_cost: number;
    premi_lebih_basis: number;
    premi_brd: number;
    total_cost: number;
    cost_per_kg: number;
}

let logCount = 0;
const MAX_LOGS = 10;

const logWithLimit = (message: string, data?: any) => {
    if (logCount < MAX_LOGS) {
        console.log(message, data);
        logCount++;
    }
};

const aggregateYtdData = (data: ProductionData[]): ProductionData[] => {
    const blockMap = new Map<string, ProductionData[]>();

    data.forEach(row => {
        const key = `${row.estate}|${row.division}|${row.block}`;
        if (!blockMap.has(key)) blockMap.set(key, []);
        blockMap.get(key)!.push(row);
    });

    const result: ProductionData[] = [];
    blockMap.forEach((rows, key) => {
        if (rows.length === 1) {
            result.push(rows[0]);
            return;
        }

        const first = rows[0];

        console.log(`[YTD Aggregate] Block: ${key} — ${rows.length} baris`);
        rows.forEach((r, i) => {
            console.log(`  [${i}] weight_act=${r.weight_act}, weight_bgt=${r.weight_bgt}, jjg_act=${r.jjg_act}, brd_qty_act=${r.brd_qty_act}`);
        });

        const harvest_area      = rows.reduce((s, r) => s + r.harvest_area, 0);
        const rotation          = rows.reduce((s, r) => s + r.rotation, 0) / rows.length;
        const hk_act            = rows.reduce((s, r) => s + r.hk_act, 0);
        const hk_bgt            = rows.reduce((s, r) => s + r.hk_bgt, 0);
        const jjg_act           = rows.reduce((s, r) => s + r.jjg_act, 0);
        const jjg_bgt           = rows.reduce((s, r) => s + r.jjg_bgt, 0);
        const weight_act        = rows.reduce((s, r) => s + r.weight_act, 0);
        const weight_bgt        = rows.reduce((s, r) => s + r.weight_bgt, 0);
        const brd_qty_act       = rows.reduce((s, r) => s + r.brd_qty_act, 0);
        const total_cost        = rows.reduce((s, r) => s + r.total_cost, 0);
        const hk_cost           = rows.reduce((s, r) => s + r.hk_cost, 0);
        const premi_lebih_basis = rows.reduce((s, r) => s + r.premi_lebih_basis, 0);
        const premi_brd         = rows.reduce((s, r) => s + r.premi_brd, 0);

        console.log(`  → SUM weight_act=${weight_act}, weight_bgt=${weight_bgt}, jjg_act=${jjg_act}, brd_qty_act=${brd_qty_act}`);

        const planted_area = first.planted_area || 1;
        const yield_act = weight_act / planted_area;
        const yield_bgt = weight_bgt / planted_area;
        const bjr_act   = jjg_act > 0 ? weight_act / jjg_act : 0;
        const bjr_bgt   = jjg_bgt > 0 ? weight_bgt / jjg_bgt : 0;

        result.push({
            estate: first.estate,
            division: first.division,
            block: first.block,
            block_status: first.block_status,
            planted_year: first.planted_year,
            planted_area: first.planted_area,
            total_planted: first.total_planted,
            sph: first.sph,
            sph_prc: first.sph_prc,
            harvest_area,
            rotation,
            hk_act,
            hk_bgt,
            hk_prc: hk_bgt > 0 ? hk_act / hk_bgt : 0,
            jjg_act,
            jjg_bgt,
            jjg_prc: jjg_bgt > 0 ? jjg_act / jjg_bgt : 0,
            weight_act,
            weight_bgt,
            weight_prc: weight_bgt > 0 ? weight_act / weight_bgt : 0,
            yield_act,
            yield_bgt,
            yield_prc: yield_bgt > 0 ? yield_act / yield_bgt : 0,
            bjr_act,
            bjr_bgt,
            bjr_prc: bjr_bgt > 0 ? bjr_act / bjr_bgt : 0,
            brd_qty_act,
            brd_prc: weight_act > 0 ? brd_qty_act / weight_act : 0,
            lebih_basis_prc: first.lebih_basis_prc,
            hk_cost,
            premi_lebih_basis,
            premi_brd,
            total_cost,
            cost_per_kg: weight_act > 0 ? total_cost / weight_act : 0,
        });
    });

    return result;
};

const getBlockColor = (data: ProductionData, jenisLaporan: string): string => {
    switch (jenisLaporan) {
        case "aktual_vs_target_ytd":
        case "aktual_vs_target_mtd":
            if ((data.weight_act / (data.weight_bgt || 1)) >= 1) return _08_GREEN_DARK;
            if ((data.weight_act / (data.weight_bgt || 1)) >= 0.9) return _07_GREEN_LIGHT;
            if ((data.weight_act / (data.weight_bgt || 1)) >= 0.8) return _05_GOLD;
            if ((data.weight_act / (data.weight_bgt || 1)) >= 0.5) return _02_RED;
            return _01_RED_DARK;

        case "yield_ytd":
            if ((data.yield_act / (data.yield_bgt || 1)) >= 1) return _08_GREEN_DARK;
            if ((data.yield_act / (data.yield_bgt || 1)) >= 0.9) return _07_GREEN_LIGHT;
            if ((data.yield_act / (data.yield_bgt || 1)) >= 0.8) return _05_GOLD;
            if ((data.yield_act / (data.yield_bgt || 1)) >= 0.5) return _02_RED;
            return _01_RED_DARK;

        case "bjr_ytd":
            if ((data.bjr_act / (data.bjr_bgt || 1)) >= 1) return _08_GREEN_DARK;
            if ((data.bjr_act / (data.bjr_bgt || 1)) >= 0.9) return _07_GREEN_LIGHT;
            if ((data.bjr_act / (data.bjr_bgt || 1)) >= 0.8) return _05_GOLD;
            if ((data.bjr_act / (data.bjr_bgt || 1)) >= 0.5) return _02_RED;
            return _01_RED_DARK;

        case "sph":
            if (data.sph / 136 >= 1) return _08_GREEN_DARK;
            if (data.sph / 136 >= 0.9) return _07_GREEN_LIGHT;
            if (data.sph / 136 >= 0.8) return _05_GOLD;
            if (data.sph / 136 >= 0.5) return _02_RED;
            return _01_RED_DARK;

        case "rotasi_panen": {
            const rot = Math.round(data.rotation * 100) / 100;
            if (rot >= 4) return _08_GREEN_DARK;
            if (rot >= 3) return _05_GOLD;
            if (rot >= 2) return _02_RED;
            return _01_RED_DARK;
        }

        case "brondolan_mtd":
            if (data.brd_qty_act / (data.weight_act || 0) < 0.05) return _02_RED;
            if (data.brd_qty_act / (data.weight_act || 0) < 0.07) return _08_GREEN_DARK;
            if (data.brd_qty_act / (data.weight_act || 0)< 0.09) return _07_GREEN_LIGHT;
            if (data.brd_qty_act / (data.weight_act || 0) < 0.12) return _06_GREEN_YELLOW;
            return _05_GOLD;

        default:
            return "#ffffff";
    }
};

// TPH point colors
const TPH_POINT_COLOR_HARVESTED = "#2196F3"; // Blue for harvested TPH (bcc > 0 & weight > 0)
const TPH_POINT_COLOR_NOT_HARVESTED = "#1a1a1a"; // Black for default/other cases
const TPH_POINT_COLOR_NO_BCC = "#ef4444"; // Red for bcc_cnt = 0 & weight > 0
const TPH_POINT_COLOR_NO_WEIGHT = "#7e22ce"; // Purple for bcc_cnt > 0 & weight = 0

const MapProduksiComponent = () => {
    const [selectedBlock, setSelectedBlock] = useState<ProductionData | null>(null);
    const [selectedTph, setSelectedTph] = useState<TphData | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<maplibregl.Map | null>(null);
    const [filters, setFilters] = useState<FilterState>({
        tahun: new Date().getFullYear().toString(),
        bulan: (new Date().getMonth() + 1).toString().padStart(2, '0'),
        jenis_laporan: "aktual_vs_target_ytd",
        tphMode: undefined,
    });
    const [pendingApply, setPendingApply] = useState(false);
    const [isSatellite, setIsSatellite] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showConnectionLines, setShowConnectionLines] = useState(true);
    const [currentView, setCurrentView] = useState<'block' | 'point' | null>(null);
    const [isLegendOpen, setIsLegendOpen] = useState(false);
    const [productionData, setProductionData] = useState<ProductionData[]>([]);
    const productionDataRef = useRef<ProductionData[]>([]);
    const [tphData, setTphData] = useState<TphData[]>([]);
    const tphDataRef = useRef<TphData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTphLoading, setIsTphLoading] = useState(false);
    const [maxWeightQty, setMaxWeightQty] = useState(1); // Default to 1 to avoid division by zero
    const isFirstRender = useRef(true);

    const sph_percent = selectedBlock ? (selectedBlock.sph / 136 || 0) : 0;
    const hk_percent = selectedBlock ? (selectedBlock.hk_act / (selectedBlock.hk_bgt || 0)) : 0;
    const jjg_percent = selectedBlock ? (selectedBlock.jjg_act / (selectedBlock.jjg_bgt || 0)) : 0;
    const weight_percent = selectedBlock ? (selectedBlock.weight_act / (selectedBlock.weight_bgt || 0)) : 0;
    const yield_percent = selectedBlock ? (selectedBlock.yield_act / (selectedBlock.yield_bgt || 0)) : 0;
    const bjr_percent = selectedBlock ? (selectedBlock.bjr_act / (selectedBlock.bjr_bgt || 0)) : 0;
    const brd_percent = selectedBlock ? (selectedBlock.brd_qty_act / (selectedBlock.weight_act || 0)) : 0;

    const defaultSidebarState = useBreakpointValue({ base: false, md: true });

    useEffect(() => {
        setSidebarOpen(defaultSidebarState ?? true);
    }, [defaultSidebarState]);

    useEffect(() => {
        productionDataRef.current = productionData;
    }, [productionData]);

    useEffect(() => {
        tphDataRef.current = tphData;
    }, [tphData]);

    const handleFilterChange = useCallback((newFilters: FilterState) => {
        setFilters(newFilters);
    }, []);

    const clearFilters = useCallback(() => {
        setFilters({});
        setPendingApply(true);
    }, []);

    const handleApplyFilters = useCallback(() => {
        setPendingApply(true);
    }, []);

    const applyBlockColors = useCallback(async (
        mapInstance: maplibregl.Map,
        data: ProductionData[],
        jenisLaporan: string
    ) => {
        const geoSource = mapInstance.getSource("geojson-source") as maplibregl.GeoJSONSource;
        if (!geoSource) return;

        const geoData = await geoSource.getData() as GeoJSON.FeatureCollection;

        geoData.features.forEach((feature: any) => {
            const blockName = feature.properties?.LandUse_26;
            const blockData = data.find(d => d.block === blockName);
            feature.properties.color = blockData
                ? getBlockColor(blockData, jenisLaporan)
                : "#ffffff";
        });

        geoSource.setData(geoData);

        mapInstance.setPaintProperty('geojson-layer', 'fill-color', [
            'case',
            ['has', 'color'],
            ['get', 'color'],
            '#ffffff'
        ]);
    }, []);

    useEffect(() => {
        if (map && productionData.length > 0) {
            applyBlockColors(map, productionData, filters.jenis_laporan || "");
        }
    }, [map, productionData, filters.jenis_laporan, applyBlockColors]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (map && filters.tahun && filters.bulan) {
            setPendingApply(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.jenis_laporan]);

    const formatNumber = (value: number, type: 'number' | 'percent' | 'currency' = 'number'): string => {
        if (type === 'percent') {
            return `${(value * 100).toFixed(2)}%`;
        }
        if (type === 'currency') {
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        }
        if (type === 'number') {
            return new Intl.NumberFormat('id-ID', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        }
        return new Intl.NumberFormat('id-ID').format(value);
    };

    const formatNumber2Digits = (value: number, type: 'number' | 'percent' | 'currency' = 'number'): string => {
        if (type === 'percent') {
            return `${(value * 100).toFixed(2)}%`;
        }
        if (type === 'currency') {
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }
        if (type === 'number') {
            return new Intl.NumberFormat('id-ID', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }
        return new Intl.NumberFormat('id-ID').format(value);
    };


    useEffect(() => {
        if (!mapContainerRef.current) return;
        const mapInstance = new maplibregl.Map({
            container: mapContainerRef.current,
            style: "https://api.maptiler.com/maps/streets-v2/style.json?key=Q3fXi2sghTMg1wQSFgxK",
            center: [104.211177, -3.141669],
            zoom: 11.5,
            attributionControl: false,
        });

        mapInstance.on("load", async () => {
            await loadGeoJSON(mapInstance);
            await fetchProductionData(mapInstance);
        });

        setMap(mapInstance);

        return () => {
            if (mapInstance) {
                mapInstance.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (map && pendingApply) {
            const fetchAllData = async () => {
                try {
                    await fetchProductionData(map);
                    if (filters.tphMode) {
                        await fetchTphData(map, filters.tphMode);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    setPendingApply(false);
                }
            };
            fetchAllData();
        }
    }, [map, pendingApply, filters]);

    // Effect to handle TPH layer removal when no TPH mode is active
    useEffect(() => {
        if (map && !filters.tphMode) {
            removeTphLayer(map);
        }
    }, [map, filters.tphMode]);

    const loadGeoJSON = async (mapInstance: maplibregl.Map) => {
        try {
            const response = await fetch("/data/batas_blok_kebun_gbs.geojson");
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const geojsonData = await response.json();

            if (!mapInstance.getSource("geojson-source")) {
                mapInstance.addSource("geojson-source", {
                    type: "geojson",
                    data: geojsonData
                });

                mapInstance.addLayer({
                    id: "geojson-layer",
                    type: "fill",
                    source: "geojson-source",
                    paint: {
                        "fill-color": "#ffffff",
                        "fill-opacity": 0.5,
                        "fill-outline-color": "#000000",
                    }
                });

                // mapInstance.addLayer({
                //     id: "geojson-outline",
                //     type: "line",
                //     source: "geojson-source",
                //     paint: {
                //         "line-color": "#000000",
                //         "line-width": 1,
                //     }
                // });

                mapInstance.addLayer({
                    id: "geojson-label",
                    type: "symbol",
                    source: "geojson-source",
                    layout: {
                        "text-field": ["get", "LandUse_26"],
                        "text-size": 14,
                        "text-anchor": "center",
                    },
                    paint: {
                        "text-color": "#000000",
                    }
                });

                mapInstance.on('click', 'geojson-layer', (e) => {
                    console.log('Click event triggered');
                    console.log('Event object:', e);

                    if (!e.features?.[0]) {
                        console.log('No features found in click event');
                        return;
                    }

                    const blockName = e.features[0].properties?.LandUse_26;
                    console.log('Clicked block name:', blockName);

                    if (!productionDataRef.current) {
                        console.log('No production data available');
                        return;
                    }

                    console.log('Current production data:', productionDataRef.current);

                    const blockData = productionDataRef.current.find(d => d.block === blockName);

                    if (blockData) {
                        console.log('Found block data:', blockData);
                        setSelectedBlock(blockData);
                    } else {
                        console.log('No data found for block:', blockName);
                    }
                });

                mapInstance.on('mouseenter', 'geojson-layer', () => {
                    mapInstance.getCanvas().style.cursor = 'pointer';
                });

                mapInstance.on('mouseleave', 'geojson-layer', () => {
                    mapInstance.getCanvas().style.cursor = '';
                });
            } else {
                (mapInstance.getSource("geojson-source") as maplibregl.GeoJSONSource).setData(geojsonData);
            }

            // Load Blok Planted LKK
            try {
                const lkkResponse = await fetch("/data/Blok_Planted_LKK.geojson");
                if (lkkResponse.ok) {
                    const lkkData = await lkkResponse.json();
                    if (!mapInstance.getSource("lkk-source")) {
                        mapInstance.addSource("lkk-source", {
                            type: "geojson",
                            data: lkkData,
                        });
                        mapInstance.addLayer({
                            id: "lkk-layer",
                            type: "fill",
                            source: "lkk-source",
                            paint: {
                                "fill-color": "#ffffff",
                                "fill-opacity": 0.5,
                                "fill-outline-color": "#000000",
                            },
                        });
                        mapInstance.addLayer({
                            id: "lkk-label",
                            type: "symbol",
                            source: "lkk-source",
                            layout: {
                                "text-field": ["get", "blok_code"],
                                "text-size": 14,
                                "text-anchor": "center",
                            },
                            paint: {
                                "text-color": "#000000",
                            },
                        });
                    } else {
                        (mapInstance.getSource("lkk-source") as maplibregl.GeoJSONSource).setData(lkkData);
                    }
                }
            } catch (err) {
                console.error("Error loading LKK GeoJSON in MapProduksi:", err);
            }

            // Load Batas MRN
            try {
                const mrnResponse = await fetch("/data/mrn_map.geojson");
                if (mrnResponse.ok) {
                    const mrnData = await mrnResponse.json();
                    if (!mapInstance.getSource("mrn-source")) {
                        mapInstance.addSource("mrn-source", {
                            type: "geojson",
                            data: mrnData,
                        });
                        mapInstance.addLayer({
                            id: "mrn-layer",
                            type: "fill",
                            source: "mrn-source",
                            paint: {
                                "fill-color": "#ffffff",
                                "fill-opacity": 0.5,
                                "fill-outline-color": "#000000",
                            },
                        });
                        mapInstance.addLayer({
                            id: "mrn-label",
                            type: "symbol",
                            source: "mrn-source",
                            layout: {
                                "text-field": ["coalesce", ["get", "blok_code"], ["get", "LandUse_26"], ""],
                                "text-size": 14,
                                "text-anchor": "center",
                            },
                            paint: {
                                "text-color": "#000000",
                            },
                        });
                    } else {
                        (mapInstance.getSource("mrn-source") as maplibregl.GeoJSONSource).setData(mrnData);
                    }
                }
            } catch (err) {
                console.error("Error loading MRN GeoJSON in MapProduksi:", err);
            }
        } catch (error) {
            console.error("Error loading GeoJSON file:", error);
        }
    };

    const fetchProductionData = async (mapInstance: maplibregl.Map) => {
        try {
            if (!filters.tahun || !filters.bulan) {
                logWithLimit('No tahun or bulan selected, skipping API call');
                return;
            }

            setIsLoading(true);

            // Construct period from separated tahun and bulan
            const tahun_bulan = `${filters.tahun}${filters.bulan}`;
            const endPeriod = tahun_bulan;
            const isYtd = ["aktual_vs_target_ytd", "yield_ytd", "bjr_ytd", "rotasi_panen"].includes(filters.jenis_laporan || "");
            const startPeriod = isYtd ? `${filters.tahun}01` : tahun_bulan;

            logWithLimit('Fetching production data with params:', {
                start_period: startPeriod,
                end_period: endPeriod,
                estate: filters.estate,
            });

            const response = await api.get('/odoo/api/report-produksi', {
                params: {
                    start_period: startPeriod,
                    end_period: endPeriod,
                    estate: filters.estate,
                }
            });

            logWithLimit('API Response:', response.data);

            if (!response.data || !response.data.data) {
                console.error('Invalid API response:', response.data);
                return;
            }

            const rawData: ProductionData[] = response.data.data;
            console.log(`[fetchProductionData] raw rows: ${rawData.length}, isYtd: ${isYtd}`);
            const data = isYtd ? aggregateYtdData(rawData) : rawData;
            console.log(`[fetchProductionData] after aggregate: ${data.length} blocks`);
            setProductionData(data);
        } catch (error) {
            console.error("Error fetching production data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTphData = async (mapInstance: maplibregl.Map, mode: "simple" | "trace") => {
        try {
            if (!filters.tahun || !filters.bulan) {
                logWithLimit('No tahun or bulan selected, skipping TPH API call');
                return;
            }

            setIsTphLoading(true);

            // Format period from tahun and bulan
            const period = `${filters.tahun}${filters.bulan}`;

            logWithLimit('Fetching TPH data with params:', {
                period,
                company_id: 1
            });

            const response = await api.get('/api/report-tph-production', {
                params: {
                    period,
                    company_id: 1
                }
            });

            logWithLimit('TPH API Response count:', response.data?.count);

            if (!response.data || !response.data.data) {
                console.error('Invalid TPH API response:', response.data);
                return;
            }

            const data: TphData[] = response.data.data;
            setTphData(data);

            // Calculate max weight_qty from all TPH data
            const maxWeight = Math.max(...data.map(tph => tph.weight_qty), 1);
            setMaxWeightQty(maxWeight);

            // Add TPH points layer to map with calculated maxWeight
            addTphLayer(mapInstance, data, maxWeight, mode);

        } catch (error) {
            console.error("Error fetching TPH data:", error);
        } finally {
            setIsTphLoading(false);
        }
    };

    const addTphLayer = (mapInstance: maplibregl.Map, data: TphData[], maxWeight: number, mode: "simple" | "trace") => {
        // Remove existing TPH layer and source if they exist
        removeTphLayer(mapInstance);

        // Create GeoJSON from TPH data
        const geojsonData: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: data
                .filter(tph => tph.lat && tph.long) // Filter out invalid coordinates
                .map(tph => ({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [tph.long, tph.lat]
                    },
                    properties: {
                        tph: tph.tph,
                        block: tph.block,
                        harvester: tph.harvester,
                        bcc_cnt: tph.bcc_cnt,
                        avg_harvest_area: tph.avg_harvest_area,
                        jjg_qty: tph.jjg_qty,
                        brd_qty: tph.brd_qty,
                        weight_qty: tph.weight_qty,
                        bjr: tph.bjr,
                        hasPositiveBcc: tph.bcc_cnt > 0,
                        hasPositiveWeight: tph.weight_qty > 0,
                        hasZeroWeightWithBcc: tph.bcc_cnt > 0 && tph.weight_qty === 0,
                        mode,
                        // Pre-calculate radius values for different zoom levels
                        radius_z10: Math.max(2, Math.round((tph.weight_qty * (32 / maxWeight) + 2) * 0.25)),
                        radius_z14: Math.max(4, Math.round((tph.weight_qty * (32 / maxWeight) + 4) * 0.5)),
                        radius_z18: Math.max(8, Math.round((tph.weight_qty * (32 / maxWeight) + 8) * 1.5))
                    }
                }))
        };

        // Add source
        mapInstance.addSource("tph-source", {
            type: "geojson",
            data: geojsonData
        });

        // Add circle layer for TPH points with dynamic radius based on weight
        mapInstance.addLayer({
            id: "tph-layer",
            type: "circle",
            source: "tph-source",
            paint: {
                "circle-radius": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10, ["get", "radius_z10"],
                    14, ["get", "radius_z14"],
                    18, ["get", "radius_z18"]
                ],
                "circle-color": [
                    "case",
                    ["==", ["get", "mode"], "simple"],
                    [
                        "case",
                        ["get", "hasPositiveWeight"],
                        TPH_POINT_COLOR_HARVESTED,
                        TPH_POINT_COLOR_NOT_HARVESTED
                    ],
                    ["all", ["!", ["get", "hasPositiveBcc"]], ["get", "hasPositiveWeight"]],
                    TPH_POINT_COLOR_NO_BCC,
                    ["all", ["get", "hasPositiveBcc"], ["get", "hasPositiveWeight"]],
                    TPH_POINT_COLOR_HARVESTED,
                    ["get", "hasZeroWeightWithBcc"],
                    TPH_POINT_COLOR_NO_WEIGHT,
                    TPH_POINT_COLOR_NOT_HARVESTED
                ],
                "circle-stroke-width": 1,
                "circle-stroke-color": "#ffffff",
                "circle-stroke-opacity": [
                    "step",
                    ["zoom"],
                    0,    // Below zoom 14, stroke is hidden
                    14, 1 // At zoom 14+, stroke is fully visible
                ],
                "circle-opacity": 0.8
            }
        });

        // Add label layer for TPH names
        mapInstance.addLayer({
            id: "tph-label-layer",
            type: "symbol",
            source: "tph-source",
            layout: {
                "text-field": ["get", "tph"],
                "text-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10, 6,   // At zoom 10, text size is 6px
                    14, 10,  // At zoom 14, text size is 10px
                    18, 14   // At zoom 18, text size is 14px
                ],
                "text-anchor": "top",
                "text-offset": [0, 0.8], // Offset below the circle
                "text-allow-overlap": false,
                "text-ignore-placement": false,
                "text-optional": true, // Hide label if it overlaps
                "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"]
            },
            paint: {
                "text-color": "#333333",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1.5,
                "text-opacity": [
                    "step",
                    ["zoom"],
                    0,    // Below zoom 18, labels are hidden
                    14, 1 // At zoom 18+, labels are fully visible
                ]
            }
        });

        // Add click handler for TPH points
        mapInstance.on('click', 'tph-layer', (e) => {
            if (!e.features?.[0]) return;

            const properties = e.features[0].properties;
            const tphName = properties?.tph;

            if (!tphDataRef.current) return;

            const tphItem = tphDataRef.current.find(t => t.tph === tphName);
            if (tphItem) {
                setSelectedTph(tphItem);
            }
        });

        // Change cursor on hover
        mapInstance.on('mouseenter', 'tph-layer', () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'tph-layer', () => {
            mapInstance.getCanvas().style.cursor = '';
        });
    };

    const removeTphLayer = (mapInstance: maplibregl.Map) => {
        // Remove label layer first
        if (mapInstance.getLayer('tph-label-layer')) {
            mapInstance.removeLayer('tph-label-layer');
        }
        // Remove circle layer
        if (mapInstance.getLayer('tph-layer')) {
            mapInstance.removeLayer('tph-layer');
        }
        // Remove source
        if (mapInstance.getSource('tph-source')) {
            mapInstance.removeSource('tph-source');
        }
    };

    const focusToArea = () => {
        if (map) {
            map.flyTo({
                center: [104.211177, -3.141669],
                zoom: 13,
                essential: true,
            });
        }
    };

    const zoomIn = () => {
        if (map) map.zoomIn();
    };

    const zoomOut = () => {
        if (map) map.zoomOut();
    };

    // Add this useEffect to track selectedBlock changes
    useEffect(() => {
        console.log('Selected block changed:', selectedBlock);
    }, [selectedBlock]);

    const getLegendItems = () => {
        switch (filters.jenis_laporan) {
            case "aktual_vs_target_ytd":
            case "aktual_vs_target_mtd":
            case "yield_ytd":
            case "bjr_ytd":
            case "sph":
                return [
                    { color: _08_GREEN_DARK, label: "≥ 100%" },
                    { color: _07_GREEN_LIGHT, label: "≥ 90%" },
                    { color: _05_GOLD, label: "≥ 80%" },
                    { color: _02_RED, label: "≥ 50%" },
                    { color: _01_RED_DARK, label: "< 50%" }
                ];
            case "rotasi_panen":
                return [
                    { color: _08_GREEN_DARK, label: "≥ 4" },
                    { color: _05_GOLD, label: "≥ 3" },
                    { color: _02_RED, label: "≥ 2" },
                    { color: _01_RED_DARK, label: "< 2" }
                ];
            case "brondolan_mtd":
                return [
                    { color: _02_RED, label: "< 5%" },
                    { color: _08_GREEN_DARK, label: "< 7%" },
                    { color: _07_GREEN_LIGHT, label: "< 9%" },
                    { color: _06_GREEN_YELLOW, label: "< 12%" },
                    { color: _05_GOLD, label: "≥ 12%" }
                ];
            default:
                return [];
        }
    };

    const formatReportType = (type: string | undefined): string => {
        switch (type) {
            case 'aktual_vs_target_ytd':
                return 'Aktual vs Target Produksi TBS YTD';
            case 'aktual_vs_target_mtd':
                return 'Aktual vs Target Produksi TBS MTD';
            case 'yield_ytd':
                return 'Yield YTD';
            case 'bjr_ytd':
                return 'BJR YTD';
            case 'sph':
                return 'SPH';
            case 'rotasi_panen':
                return 'Rotasi Panen';
            case 'brondolan_mtd':
                return 'Brondolan MTD';
            default:
                return type ?? '';
        }
    };

    return (
        // Main Container
        <Box position="static" h="100%" overflow="hidden">
            {/* Header */}
            <Flex h="full">
                {/* Sidebar */}
                <Box
                    position={{ base: 'fixed', md: 'fixed' }}
                    top={{ base: '60px', md: '64px' }}
                    left={{ base: sidebarOpen ? 0 : '-100%', md: sidebarOpen ? 0 : -350 }}
                    width={{ base: 'calc(100% - 64px)', md: '350px' }}
                    height={{ base: '100vh', md: 'calc(100vh - 64px)' }}
                    bg="white"
                    boxShadow="lg"
                    zIndex={15}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                    {sidebarOpen && (
                        <FilterProduksi
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={clearFilters}
                            onApplyFilters={handleApplyFilters}
                        />
                    )}
                </Box>

                {/* Toggle Sidebar Button */}
                <Box
                    position="fixed"
                    top={{ base: "48px", md: "34px" }}
                    left={{
                        base: sidebarOpen ? 'calc(100% - 56px)' : "10px",
                        md: sidebarOpen ? "360px" : "10px"
                    }}
                    zIndex={200}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                    {sidebarOpen ? (
                        <IconButton
                            aria-label="Sembunyikan Sidebar"
                            icon={<MdOutlineKeyboardArrowLeft />}
                            size="lg"
                            top={{ base: 5, md: 10 }}
                            onClick={() => setSidebarOpen(false)}
                            borderRadius="md"
                            boxShadow="md"
                            // bg="customGreen.500"
                            color="white"
                            _hover={{ bg: "customGreen.600" }}
                        />
                    ) : (
                        <IconButton
                            aria-label="Tampilkan Sidebar"
                            icon={<MdOutlineKeyboardArrowRight />}
                            size="lg"
                            top={{ base: "20px", md: 10 }}
                            onClick={() => setSidebarOpen(true)}
                            borderRadius="md"
                            boxShadow="md"
                            bg="customGreen.500"
                            color="white"
                            _hover={{ bg: "customGreen.600" }}
                        />
                    )}
                </Box>

                {/* Map Container */}
                <Box
                    flex={1}
                    position="relative"
                    ml={{ base: 0, md: sidebarOpen ? '350px' : 0 }}
                // transition="margin 0.3s"
                >
                    <Box
                        ref={mapContainerRef}
                        id="map"
                        h="100%"
                        w="100%"
                        sx={{
                            ".maplibre-gl-canvas": {
                                transition: "filter 0.3s",
                            },
                            "&:hover .maplibre-gl-canvas": {
                                filter: "brightness(0.98)",
                            },
                        }}
                    />
                    <Flex
                        position="absolute"
                        bottom={{ base: "15%", sm: "15%", md: "14%" }} // lebih natural di berbagai ukuran layar
                        right={{ base: "12px", sm: "13px", md: 8 }}
                        direction="column"
                        gap={2}
                        zIndex={10}
                    >
                        <IconButton
                            aria-label="Toggle legend"
                            icon={<MdOutlineLegendToggle size={20} />}
                            onClick={() => setIsLegendOpen(!isLegendOpen)}
                            variant="solid"
                            colorScheme="blue"
                            borderRadius="full"
                            boxShadow="lg"
                            zIndex={10}
                            w="40px"
                            h="40px"
                        />
                        <IconButton
                            aria-label="Focus area"
                            icon={<MdMyLocation size={20} />}
                            onClick={focusToArea}
                            variant="solid"
                            colorScheme="blue"
                            borderRadius="full"
                            boxShadow="lg"
                            w="40px"
                            h="40px"
                        />
                        <ButtonGroup
                            variant="solid"
                            colorScheme="blue"
                            isAttached
                            borderRadius="full"
                            boxShadow="lg"
                            orientation="vertical"
                        >
                            <IconButton
                                aria-label="Zoom in"
                                icon={<FiPlus size={20} />}
                                onClick={zoomIn}
                                borderBottomRadius="0"
                                w="40px"
                                h="40px"
                            />
                            <IconButton
                                aria-label="Zoom out"
                                icon={<FiMinus size={20} />}
                                onClick={zoomOut}
                                borderTopRadius="0"
                                w="40px"
                                h="40px"
                            />
                        </ButtonGroup>
                    </Flex>
                </Box>
                {/* Legend */}
                <Box
                    position="absolute"
                    bottom={{ base: "263px", md: "255px" }} // jarak dari atas layar
                    // right="5%"
                    right={{ base: "70px", md: "80px" }}
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="lg"
                    zIndex={10}
                    display={isLegendOpen ? "block" : "none"}
                >
                    <VStack align="start" spacing={2}>
                        <Text fontWeight="bold" fontSize="sm">
                            {filters.jenis_laporan === "aktual_vs_target_ytd" || filters.jenis_laporan === "aktual_vs_target_mtd"
                                ? "Persentase Berat"
                                : filters.jenis_laporan === "yield_ytd"
                                    ? "Persentase Yield"
                                    : filters.jenis_laporan === "bjr_ytd"
                                        ? "Persentase BJR"
                                        : filters.jenis_laporan === "sph"
                                            ? "Persentase SPH"
                                            : filters.jenis_laporan === "rotasi_panen"
                                                ? "Rotasi Panen"
                                                : filters.jenis_laporan === "brondolan_mtd"
                                                    ? "Persentase Brondolan"
                                                    : "Legend"}
                        </Text>
                        {getLegendItems().map((item, index) => (
                            <HStack key={index} spacing={2}>
                                <Box w={4} h={4} bg={item.color} borderRadius="sm" />
                                <Text fontSize="sm">{item.label}</Text>
                            </HStack>
                        ))}

                        {/* TPH Legend - only show when TPH points are enabled */}
                        {filters.tphMode && (
                            <>
                                <Box borderTop="1px" borderColor="gray.200" pt={2} mt={2} w="full">
                                    <Text fontWeight="bold" fontSize="sm" mb={2}>Titik TPH</Text>
                                </Box>
                                <HStack spacing={2}>
                                    <Box w={3} h={3} bg={TPH_POINT_COLOR_HARVESTED} borderRadius="full" border="1px solid white" />
                                    <Text fontSize="sm">Panen</Text>
                                </HStack>
                                {filters.tphMode === 'trace' && (
                                    <>
                                        <HStack spacing={2}>
                                            <Box w={3} h={3} bg={TPH_POINT_COLOR_NO_BCC} borderRadius="full" border="1px solid white" />
                                            <Text fontSize="sm">BKM panen belum diinput</Text>
                                        </HStack>
                                        <HStack spacing={2}>
                                            <Box w={3} h={3} bg={TPH_POINT_COLOR_NO_WEIGHT} borderRadius="full" border="1px solid white" />
                                            <Text fontSize="sm">SPB Belum diinput</Text>
                                        </HStack>
                                    </>
                                )}
                                <HStack spacing={2}>
                                    <Box w={3} h={3} bg={TPH_POINT_COLOR_NOT_HARVESTED} borderRadius="full" border="1px solid white" />
                                    <Text fontSize="sm">Tidak ada panen</Text>
                                </HStack>
                            </>
                        )}
                    </VStack>
                </Box>
            </Flex>

            {/* TPH Detail Modal */}
            <Modal
                isOpen={!!selectedTph}
                onClose={() => setSelectedTph(null)}
                size={{ base: 'md', md: 'md' }}
                isCentered
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent borderRadius="lg" overflow="hidden">
                    <ModalHeader
                        bg="customGold.500"
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                        py={{ base: 3, md: 4 }}
                        pr={{ base: 16, md: 10 }}
                    >
                        <HStack spacing={2}>
                            <Heading color="white" size="sm">
                                Detail TPH - {selectedTph?.tph}
                            </Heading>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="black" right={4} />
                    <ModalBody p={4}>
                        <VStack spacing={4} align="stretch">
                            {/* Location Information */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={2} color="gray.700">Lokasi</Heading>
                                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                    <DetailItem label="TPH" value={selectedTph?.tph} />
                                    <DetailItem label="Blok" value={selectedTph?.block} />
                                    <DetailItem label="Latitude" value={selectedTph?.lat?.toFixed(6)} />
                                    <DetailItem label="Longitude" value={selectedTph?.long?.toFixed(6)} />
                                </Grid>
                            </Box>

                            {/* Harvester Information */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={2} color="gray.700">Pemanen</Heading>
                                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                    <DetailItem
                                        label="Nama Pemanen"
                                        value={selectedTph?.harvester || '-'}
                                    />
                                    <DetailItem
                                        label="Jumlah Panen (BCC)"
                                        value={formatNumber(selectedTph?.bcc_cnt || 0)}
                                    />
                                </Grid>
                            </Box>

                            {/* Production Metrics */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={2} color="gray.700">Produksi</Heading>
                                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                    <DetailItem
                                        label="Rata-rata Luas Panen (Ha)"
                                        value={formatNumber2Digits(selectedTph?.avg_harvest_area || 0)}
                                    />
                                    <DetailItem
                                        label="Jumlah Janjang (JJG)"
                                        value={formatNumber(selectedTph?.jjg_qty || 0)}
                                    />
                                    <DetailItem
                                        label="Brondolan (kg)"
                                        value={formatNumber(selectedTph?.brd_qty || 0)}
                                    />
                                    <DetailItem
                                        label="Berat Total (kg)"
                                        value={formatNumber2Digits(selectedTph?.weight_qty || 0)}
                                    />
                                    <DetailItem
                                        label="BJR (kg)"
                                        value={formatNumber2Digits(selectedTph?.bjr || 0)}
                                    />
                                </Grid>
                            </Box>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Detail Modal */}
            <Modal
                isOpen={!!selectedBlock}
                onClose={() => setSelectedBlock(null)}
                size={{ base: 'lg', md: 'lg' }}
                isCentered
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent borderRadius="lg" overflow="hidden">
                    <ModalHeader
                        bg="customGold.500"
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                        py={{ base: 3, md: 4 }}
                        pr={{ base: 16, md: 10 }}
                    >
                        <HStack spacing={2}>
                            <Heading color="white" size="sm">
                                {formatReportType(filters.jenis_laporan)} - {filters.tahun && filters.bulan ?
                                    `${new Date(Number.parseInt(filters.tahun), Number.parseInt(filters.bulan) - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}`
                                    : ''}
                            </Heading>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="black" right={4} />
                    <ModalBody p={4}>
                        <VStack spacing={4} align="stretch">
                            {/* Basic Information */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={0} color="gray.700">Lokasi</Heading>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={-2}>
                                    <DetailItem label="Estate" value={selectedBlock?.estate} />
                                    <DetailItem label="Divisi" value={selectedBlock?.division} />
                                    <DetailItem label="Blok" value={selectedBlock?.block} />
                                </Grid>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                                    <DetailItem label="Status Blok" value={selectedBlock?.block_status} />
                                    <DetailItem label="Tahun Tanam" value={selectedBlock?.planted_year} />
                                    <DetailItem label="Luas Tanam (Ha)" value={formatNumber2Digits(selectedBlock?.planted_area || 0)} />
                                </Grid>
                            </Box>

                            {/* Planting Information */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={0} color="gray.700">Tanam</Heading>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                                    <DetailItem label="Total Tanam (pkk)" value={formatNumber(selectedBlock?.total_planted || 0)} />
                                    <DetailItem label="SPH (pkk)" value={formatNumber(selectedBlock?.sph || 0)} />
                                    <DetailItem label="% SPH" value={formatNumber(sph_percent || 0, 'percent')} />
                                </Grid>
                            </Box>

                            {/* Harvest Information */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={0} color="gray.700">Panen</Heading>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={-2}>
                                    <DetailItem label="Luas Panen (Ha)" value={formatNumber2Digits(selectedBlock?.harvest_area || 0)} />
                                    <DetailItem label="Rotasi" value={`${formatNumber2Digits(selectedBlock?.rotation || 0)} x`} />
                                </Grid>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={-2}>
                                    <DetailItem label="HK/Ha Aktual" value={formatNumber2Digits(((selectedBlock?.hk_act ?? 0) / (selectedBlock?.harvest_area ?? 0)) || 0)} />
                                    <DetailItem label="HK/Ha Budget" value={formatNumber2Digits((selectedBlock?.hk_bgt ?? 0) / (selectedBlock?.harvest_area ?? 0) || 0)} />
                                    <DetailItem label="% HK/Ha" value={formatNumber2Digits(hk_percent, 'percent')} />
                                </Grid>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                                    <DetailItem label="JJG Aktual" value={formatNumber(selectedBlock?.jjg_act || 0)} />
                                    <DetailItem label="JJG Budget" value={formatNumber(selectedBlock?.jjg_bgt || 0)} />
                                    <DetailItem label="% JJG" value={formatNumber2Digits(jjg_percent, 'percent')} />
                                </Grid>

                            </Box>

                            {/* Production Metrics */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={0} color="gray.700">Metrik Produksi</Heading>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={-2}>
                                    <DetailItem label="Berat Aktual (kg)" value={formatNumber(selectedBlock?.weight_act || 0)} />
                                    <DetailItem label="Berat Budget (kg)" value={formatNumber(selectedBlock?.weight_bgt || 0)} />
                                    <DetailItem label="% Berat" value={formatNumber2Digits(weight_percent, 'percent')} />
                                </Grid>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={-2}>
                                    <DetailItem label="Yield Aktual (ton)" value={formatNumber2Digits((selectedBlock?.yield_act ?? 0) / 1000 || 0)} />
                                    <DetailItem label="Yield Budget (ton)" value={formatNumber2Digits((selectedBlock?.yield_bgt ?? 0) / 1000 || 0)} />
                                    <DetailItem label="% Yield" value={formatNumber2Digits(yield_percent, 'percent')} />
                                </Grid>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={-2}>
                                    <DetailItem label="BJR Aktual" value={formatNumber2Digits(selectedBlock?.bjr_act || 0)} />
                                    <DetailItem label="BJR Budget" value={formatNumber2Digits(selectedBlock?.bjr_bgt || 0)} />
                                    <DetailItem label="% BJR" value={formatNumber2Digits(bjr_percent, 'percent')} />
                                </Grid>
                            </Box>

                            {/* Brondolan Information */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={0} color="gray.700">Brondolan</Heading>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                                    <DetailItem label="Brondolan Aktual (kg)" value={formatNumber(selectedBlock?.brd_qty_act || 0)} />
                                    <DetailItem label="% Brondolan" value={formatNumber2Digits(brd_percent, 'percent')} />
                                    {/* <DetailItem label="% Lebih Basis" value={formatNumber2Digits(selectedBlock?.lebih_basis_prc || 0, 'percent')} /> */}
                                </Grid>
                            </Box>

                            {/* Cost Information */}
                            <Box>
                                <Heading size={{ base: 'xs', md: 'sm' }} mb={0} color="gray.700">Biaya</Heading>
                                <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={-2}>
                                    <DetailItem label="Total Biaya" value={formatNumber(selectedBlock?.total_cost || 0, 'currency')} />
                                    <DetailItem label="Biaya HK per kg" value={formatNumber(selectedBlock?.cost_per_kg || 0, 'currency')} />
                                </Grid>
                            </Box>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Loading Overlay */}
            {(isLoading || isTphLoading) && (
                <Center
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(255, 255, 255, 0.8)"
                    zIndex={2000}
                >
                    <VStack spacing={4}>
                        <Spinner
                            thickness="4px"
                            speed="0.65s"
                            emptyColor="gray.200"
                            color="blue.500"
                            size="xl"
                        />
                        <Text fontWeight="medium" color="gray.700">
                            {isTphLoading ? 'Loading TPH data...' : 'Loading data...'}
                        </Text>
                    </VStack>
                </Center>
            )}
        </Box>
    );
};

const DetailItem = ({
    label,
    value,
    type = 'number',
}: {
    label: string;
    value?: string | number;
    type?: 'number' | 'percent' | 'currency';
}) => (
    <Box p={2} bg="white" borderRadius="md" _hover={{ bg: 'gray.50' }}>
        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" mb={1}>
            {label}
        </Text>
        <Text
            fontSize={{ base: 'xs', md: 'sm' }}
            fontWeight="500"
            color={
                type === 'percent'
                    ? Number(value) >= 1
                        ? 'green.600'
                        : Number(value) >= 0.9
                            ? 'green.500'
                            : Number(value) >= 0.8
                                ? 'yellow.500'
                                : 'red.500'
                    : 'gray.800'
            }
        >
            {value ?? '-'}
        </Text>
    </Box>
);

export default MapProduksiComponent;
